import { useState } from 'react';
import { useParams, useOutletContext, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useFamilyDetailQuery } from './useFamilyDetailQuery';
import FamilyGeneralTab from './FamilyGeneralTab';
import FamilyEnrollmentTab from './FamilyEnrollmentTab';
import BillingTab from './BillingTab';
import AddStudentsToFamilyWizard from './AddStudentsToFamilyWizard';
import type { AppLayoutContext } from '~/types';
import type { ChildPerson } from './types';

function FamilyDetail() {
  const { familySlug } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const location = useLocation();
  const navigate = useNavigate();

  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Determine active tab from URL
  const isEnrollmentTab = location.pathname.endsWith('/enrollment');
  const isBillingTab = location.pathname.endsWith('/billing');
  const activeTab = isBillingTab ? 'billing' : isEnrollmentTab ? 'enrollment' : 'general';

  const selectedYear = roleContext?.selectedSchoolYear;

  const { data, loading, error, refetch } = useFamilyDetailQuery(
    school.id,
    familySlug,
    selectedYear?.id || undefined,
  );

  const family = data?.school?.family;
  const enrollment = family?.familyEnrollmentForYear;
  const gradeLevels = data?.school?.gradeLevelsSetting?.items || [];
  // The plans of the family's year, which the special payment options and invoices offer.
  const paymentOptions = enrollment?.schoolYear?.tuitionPaymentOptionsSetting || [];
  // The invoice-set wizard splits a total across the students on this year's
  // enrollment, so BillingTab needs them by person id.
  const enrolledStudents = (enrollment?.studentEnrollments || []).map((studentEnrollment) => ({
    id: studentEnrollment.person.id,
    firstName: studentEnrollment.person.firstName,
    lastName: studentEnrollment.person.lastName,
  }));

  const handleTabChange = (value: string) => {
    const basePath = `/app/${school.slug}/families/${familySlug}`;
    if (value === 'enrollment') {
      navigate(`${basePath}/enrollment`);
    } else if (value === 'billing') {
      navigate(`${basePath}/billing`);
    } else {
      navigate(basePath);
    }
  };

  const handleAddChildToYear = (_person: ChildPerson) => {
    // Open wizard with this child pre-selected
    setIsWizardOpen(true);
  };

  const handleOpenAddStudentsWizard = () => {
    setIsWizardOpen(true);
  };

  const handleWizardClose = (didCreate: boolean) => {
    setIsWizardOpen(false);
    if (didCreate) {
      refetch();
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted rounded mb-6"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Link to={`/app/${school.slug}/families`}>
          <Button variant="ghost" className="-ml-4 mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Families
          </Button>
        </Link>
        <div className="text-destructive">
          Error loading family: {error.message}
        </div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="p-6">
        <Link to={`/app/${school.slug}/families`}>
          <Button variant="ghost" className="-ml-4 mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Families
          </Button>
        </Link>
        <div className="text-muted-foreground">
          Family not found.
        </div>
      </div>
    );
  }

  // Get available children for the wizard (those not yet enrolled)
  const enrolledPersonIds = new Set(
    (enrollment?.studentEnrollments || []).map((se) => se.person.id)
  );
  const availableChildren = (family.children || [])
    .filter((child) => !enrolledPersonIds.has(child.person.id))
    .map((child) => ({
      id: child.person.id,
      firstName: child.person.firstName,
      lastName: child.person.lastName,
      familyName: family.name,
    }));

  // Determine back navigation based on current tab
  const backUrl = isEnrollmentTab && selectedYear
    ? `/app/${school.slug}/years/${selectedYear.slug}/enrollments`
    : `/app/${school.slug}/families`;
  const backLabel = isEnrollmentTab && selectedYear
    ? 'Back to Enrollments'
    : 'Back to Families';

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to={backUrl}>
          <Button variant="ghost" className="-ml-4 mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLabel}
          </Button>
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {family.name}
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/app/${school.slug}/view-as-family/${family.id}`}>View as family</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/app/${school.slug}/view-as-family/${family.id}/invoices`}>View invoices as family</Link>
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="enrollment">
            Enrollment {selectedYear ? `(${selectedYear.name})` : ''}
          </TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-6">
          <FamilyGeneralTab
            family={family}
            schoolSlug={school.slug}
            checklistEnrollmentOn={data?.school?.checklistEnrollmentOn ?? false}
            selectedYear={selectedYear}
            onAddChildToYear={handleAddChildToYear}
            onRefetch={refetch}
          />
        </TabsContent>
        <TabsContent value="enrollment" className="mt-6">
          <FamilyEnrollmentTab
            family={family}
            enrollment={enrollment}
            selectedYear={selectedYear}
            schoolSlug={school.slug}
            schoolId={school.id}
            checklistEnrollmentOn={data?.school?.checklistEnrollmentOn ?? false}
            gradeLevels={gradeLevels}
            paymentOptions={paymentOptions}
            onOpenAddStudentsWizard={handleOpenAddStudentsWizard}
            onRefetch={refetch}
          />
        </TabsContent>
        <TabsContent value="billing" className="mt-6">
          <BillingTab
            schoolId={school.id}
            familySlug={familySlug}
            schoolYearId={selectedYear?.id}
            selectedYear={selectedYear}
            isActive={activeTab === 'billing'}
            students={enrolledStudents}
            checklistEnrollmentOn={data?.school?.checklistEnrollmentOn ?? false}
            familyYear={enrollment}
          />
        </TabsContent>
      </Tabs>

      {/* Add Students Wizard */}
      {isWizardOpen && (
        <AddStudentsToFamilyWizard
          open={isWizardOpen}
          onOpenChange={handleWizardClose}
          familyId={family.id}
          familyName={family.name}
          schoolYearId={selectedYear?.id}
          gradeLevels={gradeLevels}
          availableChildren={availableChildren}
          hasExistingEnrollment={!!enrollment}
          existingEnrollmentKind={enrollment?.enrollmentKind}
          checklistEnrollmentOn={data?.school?.checklistEnrollmentOn ?? false}
        />
      )}
    </div>
  );
}

export default FamilyDetail;
